import React from "react";

export type Column<T> = {
  id: string;
  header: React.ReactNode;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  cell: (row: T) => React.ReactNode;
};

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  emptyMessage?: string;
  emptyRender?: React.ReactNode;
  className?: string;
  // Paginator (always visible – provide handlers for interaction)
  page?: number; // defaults to 1
  totalPages?: number; // defaults to 1
  onPageChange?: (page: number) => void; // if undefined, nav buttons are disabled
  itemsPerPage?: number; // defaults to 25
  onItemsPerPageChange?: (n: number) => void; // if undefined, selector is disabled
  totalResults?: number; // optional summary
  itemsPerPageOptions?: number[]; // defaults [25, 50, 100]
  // Optional: limit body height so header/footer are fixed
  maxBodyHeight?: string;
  onRowClick?: (row: T) => void;
}

function buildPageList(page: number, totalPages: number): (number | string)[] {
  const items: (number | string)[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) items.push(i);
    return items;
  }
  items.push(1);
  const showLeftDots = page > 4;
  const showRightDots = page < totalPages - 3;
  const start = Math.max(2, page - 2);
  const end = Math.min(totalPages - 1, page + 2);
  if (showLeftDots) items.push('…l');
  for (let i = start; i <= end; i++) items.push(i);
  if (showRightDots) items.push('…r');
  items.push(totalPages);
  return items;
}

export function DataTable<T>({ columns, rows, loading, emptyMessage = 'No records found', emptyRender, className = '', page = 1, totalPages = 1, onPageChange, itemsPerPage = 25, onItemsPerPageChange, totalResults, itemsPerPageOptions = [25, 50, 100], maxBodyHeight, onRowClick }: DataTableProps<T>) {
  const clampedPage = Math.max(1, Math.min(page, Math.max(1, totalPages)));
  const pageList = buildPageList(clampedPage, Math.max(1, totalPages));
  return (
    <div className={"bg-white rounded-lg shadow border border-gray-200 flex flex-col " + className}>
      <div className="overflow-auto" style={maxBodyHeight ? { maxHeight: maxBodyHeight } : undefined}>
        <table className="min-w-full divide-y divide-gray-200 table-fixed">
          <colgroup>
            {columns.map((c) => (
              <col key={c.id} style={c.width ? { width: typeof c.width === 'number' ? `${c.width}%` : c.width } : undefined} />
            ))}
          </colgroup>
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {columns.map((c) => {
                const alignClass = c.align === 'center' ? 'text-center' : c.align === 'right' ? 'text-right' : 'text-left';
                return (
                  <th key={c.id} className={`px-6 py-2 ${alignClass} text-xs font-medium text-gray-600 tracking-wide`}>{c.header}</th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && (
              <tr>
                <td colSpan={columns.length} className="py-10">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <div className="ci-dots-bars-loader" aria-hidden="true" />
                    <span className="text-sm text-gray-600">Loading...</span>
                  </div>
                </td>
              </tr>
            )}
            {!loading && rows.map((row, idx) => (
              <tr
                key={idx}
                className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={onRowClick ? (event) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onRowClick(row);
                  }
                } : undefined}
                tabIndex={onRowClick ? 0 : undefined}
              >
                {columns.map((c) => {
                  const alignClass = c.align === 'center' ? 'text-center' : c.align === 'right' ? 'text-right' : 'text-left';
                  return (
                    <td key={c.id} className={`px-6 py-2.5 whitespace-nowrap min-w-0 text-sm text-gray-900 ${alignClass}`}>
                      {c.cell(row)}
                    </td>
                  );
                })}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="py-10">
                  {emptyRender ? (
                    emptyRender
                  ) : (
                    <div className="text-center text-sm text-gray-500">{emptyMessage}</div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {
        <div className="bg-white px-4 py-2 border-t border-gray-200 flex items-center justify-between sticky bottom-0">
          <div className="flex items-center gap-2">
            <button
              disabled={clampedPage === 1 || !onPageChange}
              onClick={() => onPageChange && onPageChange(1)}
              className={`w-9 h-9 rounded flex items-center justify-center group ${clampedPage === 1 || !onPageChange ? 'opacity-40 cursor-not-allowed' : 'hover:bg-blue-50'}`}
              title="First"
            >
              <span className="group-hover:underline text-lg">«</span>
            </button>
            <button
              disabled={clampedPage === 1 || !onPageChange}
              onClick={() => onPageChange && onPageChange(Math.max(1, clampedPage - 1))}
              className={`w-9 h-9 rounded flex items-center justify-center group ${clampedPage === 1 || !onPageChange ? 'opacity-40 cursor-not-allowed' : 'hover:bg-blue-50'}`}
              title="Previous"
            >
              <span className="group-hover:underline text-lg">‹</span>
            </button>

            <div className="flex items-center gap-1">
              {pageList.map((it, idx) => it === '…l' || it === '…r' ? (
                <span key={`dots-${idx}`} className="px-1 text-sm text-gray-500">…</span>
              ) : (
                <button
                  key={`p-${it}`}
                onClick={() => onPageChange && onPageChange(it as number)}
                className={`group ${clampedPage === it ? 'w-8 h-8 rounded-full bg-[#C7DEF6] text-[#1D5176] flex items-center justify-center' : 'h-8 px-2 text-sm text-gray-700 hover:bg-blue-50 rounded'} ${!onPageChange ? 'opacity-40 cursor-not-allowed' : ''}`}
                  title={`Page ${it}`}
                disabled={!onPageChange}
                >
                <span className={`${clampedPage === it ? '' : 'group-hover:underline'}`}>{it}</span>
                {clampedPage !== it && (
                    <span className="ml-1 opacity-0 group-hover:opacity-70">›</span>
                  )}
                </button>
              ))}
            </div>

            <button
            disabled={clampedPage >= totalPages || !onPageChange}
            onClick={() => onPageChange && onPageChange(Math.min(totalPages, clampedPage + 1))}
            className={`w-9 h-9 rounded flex items-center justify-center group ${clampedPage >= totalPages || !onPageChange ? 'opacity-40 cursor-not-allowed' : 'hover:bg-blue-50'}`}
              title="Next"
            >
              <span className="group-hover:underline text-lg">›</span>
            </button>
            <button
            disabled={clampedPage >= totalPages || !onPageChange}
            onClick={() => onPageChange && onPageChange(totalPages)}
            className={`w-9 h-9 rounded flex items-center justify-center group ${clampedPage >= totalPages || !onPageChange ? 'opacity-40 cursor-not-allowed' : 'hover:bg-blue-50'}`}
              title="Last"
            >
              <span className="group-hover:underline text-lg">»</span>
            </button>
          </div>

          <div className="flex items-center gap-3">
            {typeof totalResults === 'number' && (
              <span className="text-sm text-gray-600 whitespace-nowrap">
                {loading ? 'Loading...' : `${totalResults} result(s)`}
              </span>
            )}
            {typeof totalResults === 'number' && (
              <span className="text-xs text-gray-500 whitespace-nowrap">{!loading ? `Showing ${rows.length} of ${totalResults}` : ''}</span>
            )}
          <select
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange && onItemsPerPageChange(Number(e.target.value))}
            className="text-sm border border-gray-300 rounded px-2 py-1.5"
            disabled={!onItemsPerPageChange}
          >
            {(itemsPerPageOptions || []).map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
          <span className="text-sm text-gray-600">Items per page</span>
          </div>
        </div>
      }
    </div>
  );
}

export default DataTable;


